"use client";

/**
 * Presentational, routing-agnostic paginator.
 *
 * - Wraps MUI `<Pagination>` with first/prev/window/next/last controls
 *   (siblings 1, boundary 1 → renders "1 … 4 5 6 … 12").
 * - Hidden when `count <= 1` (no useless "1 / 1").
 * - The design-system stays framework-agnostic: the app injects how links
 *   are rendered via `LinkComponent` (e.g. a Next.js `Link`) and computes the
 *   `href`/`onChange` from its own router. Extra link props (e.g.
 *   `{ scroll: false, replace: true }`) are spread through `linkProps`.
 * - `loading` marks the paginator busy (not inert — navigation still works).
 */

import {
  Box,
  Pagination as MuiPagination,
  PaginationItem,
} from "@mui/material";

import type { SxProps, Theme } from "@mui/material/styles";
import type { ElementType } from "react";

export type PaginationProps = {
  /** Total number of pages. The paginator hides itself when `count <= 1`. */
  count: number;
  /** Current 1-based page. Clamped into `[1, count]`. */
  page: number;
  /** Builds the href for a given target page. */
  getItemHref: (page: number) => string;
  /** Called with the target page when an item is activated. */
  onChange: (page: number) => void;
  /** Element used to render each item as a link (defaults to `'a'`). */
  LinkComponent?: ElementType;
  /** Extra props spread onto each item (e.g. `{ scroll: false, replace: true }`). */
  linkProps?: Record<string, unknown>;
  /** Show a subtle busy indicator while the next page is loading. */
  loading?: boolean;
  /** Optional `data-testid` to scope tests to a specific paginator. */
  "data-testid"?: string;
  /** Styles merged into the container, e.g. `{ mt: 0 }` inside a footer row. */
  sx?: SxProps<Theme>;
};

export function Pagination({
  count,
  page,
  getItemHref,
  onChange,
  LinkComponent = "a",
  linkProps,
  loading = false,
  "data-testid": testId,
  sx,
}: PaginationProps): React.JSX.Element | null {
  if (count <= 1) return null;

  const safePage = Math.min(Math.max(page, 1), Math.max(count, 1));

  return (
    <Box
      sx={[
        { display: "flex", justifyContent: "center", mt: 2 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      data-testid={testId ?? "pagination"}
    >
      <MuiPagination
        count={count}
        page={safePage}
        onChange={(_, nextPage) => onChange(nextPage)}
        showFirstButton
        showLastButton
        color="primary"
        shape="rounded"
        siblingCount={1}
        boundaryCount={1}
        disabled={loading}
        renderItem={(item) => (
          <PaginationItem
            component={LinkComponent}
            href={item.page === null ? "#" : getItemHref(item.page)}
            {...linkProps}
            {...item}
          />
        )}
      />
    </Box>
  );
}
